import { useAuth } from '../context/AuthContext';
import { useLoading } from '../context/LoadingContext';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { Plus, Search, Edit2, Trash2, Eye, Bus, MapPin, Calendar, X, ChevronLeft, ChevronRight, FileText, ClipboardList, Download, Printer, Package, Truck, MessageCircle, Ticket, Phone, User, CreditCard, Navigation, Receipt } from 'lucide-react';
import ExportFormatModal from '../components/ExportFormatModal';
import {
  exportMotoristaXLSX,
  exportBagagemXLSX,
  exportEncomendasXLSX,
  exportEmbarqueXLSX,
  exportRelatorioXLSX,
} from '../utils/excelExport';

const STATUS_OPTIONS = [
  { value: 'agendada', label: 'Agendada', cls: 'bg-blue-100 text-blue-700' },
  { value: 'em_andamento', label: 'Em Andamento', cls: 'bg-amber-100 text-amber-700' },
  { value: 'finalizada', label: 'Finalizada', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'cancelada', label: 'Cancelada', cls: 'bg-red-100 text-red-700' },
];

const getStatusCls = (s) => STATUS_OPTIONS.find(o => o.value === s)?.cls || 'bg-slate-100 text-slate-700';
const getStatusLabel = (s) => STATUS_OPTIONS.find(o => o.value === s)?.label || s;

const emptyForm = { titulo: '', origem: '', destino: '', data: '', horario: '', veiculo: '', motorista: '', capacidade: 44, observacoes: '', status: 'agendada' };

// ── Helpers de status e pagamento ────────────────────────────────────────────
function getReservaStatusStyle(status, formaPag) {
  if (formaPag === 'Cortesia') return { cls: 'bg-blue-100 text-blue-700', label: 'Cortesia' };
  if (formaPag === 'A Pagar')  return { cls: 'bg-orange-100 text-orange-700', label: 'A Pagar' };
  if (status === 'confirmada') return { cls: 'bg-emerald-100 text-emerald-700', label: 'Confirmada' };
  if (status === 'pendente')   return { cls: 'bg-amber-100 text-amber-700', label: 'Pendente' };
  if (status === 'cancelada')  return { cls: 'bg-red-100 text-red-700', label: 'Cancelada' };
  return { cls: 'bg-slate-100 text-slate-600', label: status };
}

function waLink(tel) {
  if (!tel) return null;
  const digits = tel.replace(/\D/g, '');
  return digits ? `https://wa.me/55${digits}` : null;
}

// ── Popup de info da poltrona ─────────────────────────────────────────────────
function SeatPopup({ seat, reserva, onClose, onPrint, onRecibo }) {
  const { cls, label } = getReservaStatusStyle(reserva.status, reserva.forma_pagamento);
  const wa = waLink(reserva.cliente?.telefone);
  return (
    <>
      {/* Backdrop leve para fechar clicando fora */}
      <div className="fixed inset-0 z-[60]" onClick={onClose} />

      {/* Popup centralizado */}
      <div
        className="fixed z-[61] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                   w-76 bg-white rounded-2xl shadow-2xl border border-slate-200
                   animate-in fade-in zoom-in-95 duration-150"
        style={{ width: '300px' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Topo colorido */}
        <div className="bg-gradient-to-r from-[#0147a0] to-[#0369a1] rounded-t-2xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <span className="text-white font-bold text-sm">{seat}</span>
            </div>
            <div>
              <p className="text-white/70 text-[10px] uppercase tracking-wider font-medium">Poltrona</p>
              <p className="text-white font-semibold text-sm leading-tight">{reserva.cliente?.nome || '—'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-white/60 hover:text-white hover:bg-white/20 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Corpo */}
        <div className="px-4 py-3 space-y-2.5">
          {/* Status + valor */}
          <div className="flex items-center justify-between">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>{label}</span>
            {reserva.valor > 0 && (
              <span className="text-slate-700 font-bold text-sm">R$ {Number(reserva.valor).toFixed(2)}</span>
            )}
          </div>

          <div className="space-y-2 text-xs">
            {reserva.cliente?.telefone && (
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span>{reserva.cliente.telefone}</span>
              </div>
            )}
            {reserva.forma_pagamento && reserva.forma_pagamento !== 'Cortesia' && reserva.forma_pagamento !== 'A Pagar' && (
              <div className="flex items-center gap-2 text-slate-600">
                <CreditCard className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span>{reserva.forma_pagamento}</span>
              </div>
            )}
            {reserva.local_embarque && (
              <div className="flex items-center gap-2 text-slate-600">
                <Navigation className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span>Embarque: <span className="font-medium text-slate-700">{reserva.local_embarque}</span></span>
              </div>
            )}
          </div>
        </div>

        {/* Ações — 3 botões */}
        <div className="px-4 pb-4 flex flex-col gap-2">
          <div className="flex gap-2">
            {wa && (
              <a href={wa} target="_blank" rel="noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-xl transition-colors">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
            {onPrint && (
              <button onClick={() => { onPrint(seat); onClose(); }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#0147a0] hover:bg-[#003375] text-white text-xs font-semibold rounded-xl transition-colors">
                <Ticket className="w-3.5 h-3.5" /> Passagem
              </button>
            )}
          </div>
          {onRecibo && reserva.id && (
            <button
              onClick={() => { onRecibo(reserva.id, reserva.cliente?.nome); onClose(); }}
              className="w-full flex items-center justify-center gap-1.5 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-xl transition-colors">
              <Receipt className="w-3.5 h-3.5" /> Gerar Recibo PDF
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ── Mapa visual de poltronas ──────────────────────────────────────────────────
// Layout padrão: [b+1 JAN] [b+2 COR] | corredor | [b+4 COR] [b+3 JAN]
function MiniSeatMap({ capacidade, reservas, onPrintSeat, onRecibo }) {
  const [selectedSeat, setSelectedSeat] = useState(null);

  const reservaMap = {};
  (reservas || []).filter(r => r.status !== 'cancelada').forEach(r => {
    reservaMap[r.assento] = r;
  });
  const ocupados = new Set(Object.keys(reservaMap).map(Number));
  const totalLivres = capacidade - ocupados.size;

  const numRows = Math.ceil(capacidade / 4);
  const rows = [];
  for (let i = 0; i < numRows; i++) {
    const b = i * 4;
    rows.push([b + 1, b + 2, null, b + 4, b + 3]);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-1.5">
          <Bus className="w-4 h-4 text-slate-400" />
          Mapa de Poltronas
        </h3>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{totalLivres} livres</span>
          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">{ocupados.size} ocupados</span>
        </div>
      </div>

      <div className="rounded-xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
        {/* Frente */}
        <div className="bg-gradient-to-b from-slate-700 to-slate-600 px-3 pt-2 pb-2 flex items-center gap-2">
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
            <svg viewBox="0 0 36 36" width="22" height="22" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="18" cy="18" r="16"/>
              <circle cx="18" cy="18" r="5" fill="#94a3b8" stroke="none"/>
              <line x1="18" y1="2" x2="18" y2="13"/>
              <line x1="18" y1="23" x2="18" y2="34"/>
              <line x1="2"  y1="18" x2="13" y2="18"/>
              <line x1="23" y1="18" x2="34" y2="18"/>
            </svg>
            <span className="text-slate-400 text-[7px] font-semibold uppercase tracking-widest">Motor.</span>
          </div>
          <div className="flex-1 h-7 bg-slate-500/30 rounded border border-slate-500/40 flex items-center justify-center">
            <span className="text-slate-400 text-[8px] font-medium uppercase tracking-widest">Para-brisa</span>
          </div>
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
            <div className="w-4 h-7 bg-slate-800/50 rounded-sm border border-slate-500/30 flex items-center justify-center">
              <div className="w-1 h-1 rounded-full bg-slate-500"/>
            </div>
            <span className="text-slate-400 text-[7px] font-medium uppercase tracking-widest">Porta</span>
          </div>
        </div>

        {/* Cabeçalho colunas */}
        <div className="bg-slate-100 border-b border-slate-200 px-3 py-1"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 14px 1fr 1fr', gap: '4px' }}>
          {['JAN.', 'COR.', '', 'COR.', 'JAN.'].map((h, i) => (
            <div key={i} className="text-center text-[8px] font-bold text-slate-400 uppercase tracking-wider">{h}</div>
          ))}
        </div>

        {/* Assentos */}
        <div className="px-3 py-2 space-y-1 max-h-56 overflow-y-auto">
          {rows.map((row, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 14px 1fr 1fr', gap: '4px', alignItems: 'center' }}>
              {row.map((num, j) => {
                if (num === null) return (
                  <div key={j} className="flex items-center justify-center h-7">
                    <div className="w-px h-full" style={{ background: 'repeating-linear-gradient(to bottom,#cbd5e1 0,#cbd5e1 3px,transparent 3px,transparent 6px)' }} />
                  </div>
                );
                if (num > capacidade) return <div key={j} className="h-7" />;
                const isOcc = ocupados.has(num);
                const isSel = selectedSeat === num;
                return (
                  <button
                    key={j}
                    type="button"
                    onClick={() => isOcc ? setSelectedSeat(isSel ? null : num) : null}
                    title={isOcc ? `Poltrona ${num} — clique para ver detalhes` : `Poltrona ${num} — Livre`}
                    className={`h-7 rounded-md text-[10px] font-bold flex items-center justify-center transition-all
                      ${isOcc
                        ? `bg-red-100 text-red-500 border border-red-200 cursor-pointer hover:bg-red-200 ${isSel ? 'ring-2 ring-red-400 ring-offset-1 bg-red-200' : ''}`
                        : 'bg-emerald-100 text-emerald-700 border border-emerald-300 cursor-default'
                      }`}
                  >
                    {num}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Fundo */}
        <div className="bg-slate-100 border-t border-slate-200 py-1 text-center">
          <span className="text-[7px] font-medium text-slate-400 uppercase tracking-widest">— Fundo do Ônibus —</span>
        </div>
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded inline-block"/>Livre</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-100 border border-red-200 rounded inline-block"/>Ocupado (clique para ver)</span>
      </div>

      {/* Popup flutuante ao clicar em assento ocupado */}
      {selectedSeat && reservaMap[selectedSeat] && (
        <SeatPopup
          seat={selectedSeat}
          reserva={reservaMap[selectedSeat]}
          onClose={() => setSelectedSeat(null)}
          onPrint={onPrintSeat}
          onRecibo={onRecibo}
        />
      )}
    </div>
  );
}

export default function Viagens() {
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const canWrite = user?.role !== 'financeiro';
  const [viagens, setViagens] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [exportModal, setExportModal] = useState({ open: false, viagemId: null, type: null, label: '' });
  const [loadingExcel, setLoadingExcel] = useState(false);
  const PER_PAGE = 10;

  const REPORT_LABELS = {
    motorista: 'Lista para Motorista',
    bagagem: 'Lista de Bagagem',
    encomendas: 'Lista de Encomendas',
    embarque: 'Bilhetes de Embarque',
    relatorio: 'Relatório Completo',
  };

  const openExportModal = (viagemId, type) => {
    setExportModal({ open: true, viagemId, type, label: REPORT_LABELS[type] || 'Relatório' });
  };

  const closeExportModal = () => {
    setExportModal({ open: false, viagemId: null, type: null, label: '' });
  };

  const fetchViagens = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/viagens', {
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          data_inicio: dataInicio || undefined,
          data_fim: dataFim || undefined,
        }
      });
      setViagens(data);
    } catch { toast.error('Erro ao carregar viagens'); }
    finally { setLoading(false); }
  }, [search, statusFilter, dataInicio, dataFim]);

  useEffect(() => { fetchViagens(); }, [statusFilter, dataInicio, dataFim]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (v) => { setEditingId(v.id); setForm({ ...v }); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(emptyForm); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, capacidade: parseInt(form.capacidade) };
      if (editingId) {
        await api.put(`/viagens/${editingId}`, payload);
        toast.success('Viagem atualizada com sucesso!');
      } else {
        await api.post('/viagens', payload);
        toast.success('Viagem criada com sucesso!');
      }
      closeModal();
      fetchViagens();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar viagem');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/viagens/${deleteId}`);
      toast.success('Viagem excluída!');
      setDeleteId(null);
      fetchViagens();
    } catch { toast.error('Erro ao excluir viagem'); }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = {
        search: search || undefined,
        status: statusFilter || undefined,
        data_inicio: dataInicio || undefined,
        data_fim: dataFim || undefined,
      };
      const response = await api.get('/viagens/export/csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'viagens.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado com sucesso!');
    } catch { toast.error('Erro ao exportar CSV'); }
    finally { setExporting(false); }
  };

  const downloadPDF = async (viagemId, type, assento = null) => {
    const endpointMap = {
      relatorio: 'relatorio-pdf',
      embarque: 'lista-embarque-pdf',
      motorista: 'lista-motorista/pdf',
      bagagem: 'lista-bagagem/pdf',
      encomendas: 'lista-encomendas/pdf',
    };
    const labelMap = {
      relatorio: 'Relatório de Passageiros',
      embarque: 'Lista de Embarque',
      motorista: 'Lista para Motorista',
      bagagem: 'Lista de Bagagem',
      encomendas: 'Lista de Encomendas',
    };
    const endpoint = endpointMap[type] || 'relatorio-pdf';
    const label = labelMap[type] || 'PDF';
    showLoading();
    try {
      const params = assento ? { assentos: assento } : {};
      const response = await api.get(`/viagens/${viagemId}/${endpoint}`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const filename = assento ? `embarque-poltrona${assento}-${viagemId}.pdf` : `${type}-${viagemId}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(assento ? `Passagem da poltrona ${assento} gerada!` : `${label} gerado com sucesso!`);
    } catch {
      toast.error(`Erro ao gerar ${label}`);
    } finally {
      hideLoading();
    }
  };

  const downloadRecibo = async (reservaId, nomePassageiro) => {
    showLoading();
    try {
      const response = await api.get(`/reservas/${reservaId}/recibo-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recibo-${nomePassageiro || reservaId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Recibo gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar recibo');
    } finally {
      hideLoading();
    }
  };

  const downloadExcel = async (viagemId, type) => {
    setLoadingExcel(true);
    try {
      const { data: viagem } = await api.get(`/viagens/${viagemId}`);

      if (type === 'motorista') {
        exportMotoristaXLSX(viagem);
      } else if (type === 'embarque') {
        exportEmbarqueXLSX(viagem);
      } else if (type === 'relatorio') {
        exportRelatorioXLSX(viagem);
      } else if (type === 'bagagem') {
        const { data: bagagens } = await api.get('/bagagens', { params: { viagem_id: viagemId } });
        exportBagagemXLSX(viagem, bagagens);
      } else if (type === 'encomendas') {
        const { data: encomendas } = await api.get('/encomendas', { params: { viagem_id: viagemId } });
        exportEncomendasXLSX(viagem, encomendas);
      }

      toast.success(`${REPORT_LABELS[type] || 'Relatório'} exportado em Excel!`);
    } catch {
      toast.error('Erro ao gerar Excel');
    } finally {
      setLoadingExcel(false);
    }
  };

  const handleViewDetail = async (id) => {
    try {
      const { data } = await api.get(`/viagens/${id}`);
      setViewModal(data);
    } catch { toast.error('Erro ao carregar detalhes'); }
  };

  const paginated = viagens.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(viagens.length / PER_PAGE);

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-5 page-enter" data-testid="viagens-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Viagens</h1>
          <p className="text-slate-500 text-sm mt-0.5">{viagens.length} viagem(ns) encontrada(s)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportCSV} disabled={exporting} data-testid="btn-export-viagens-csv"
            className="flex items-center gap-2 border border-slate-300 text-slate-600 hover:bg-slate-50 font-medium px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-60">
            <Download className="w-4 h-4" /> {exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
          {canWrite && (
          <button onClick={openCreate} data-testid="btn-nova-viagem"
            className="flex items-center gap-2 bg-[#d72d21] hover:bg-[#b91c1c] text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm">
            <Plus className="w-4 h-4" /> Nova Viagem
          </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchViagens()}
                placeholder="Buscar por título, origem ou destino..." data-testid="viagens-search-input"
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="viagens-status-filter"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white">
              <option value="">Todos os status</option>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex items-center gap-2 flex-1">
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-500 mb-1">Data início</label>
                <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)}
                  data-testid="viagens-data-inicio-filter"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
              </div>
              <span className="text-slate-400 text-sm mt-4">até</span>
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-500 mb-1">Data fim</label>
                <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)}
                  data-testid="viagens-data-fim-filter"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
              </div>
            </div>
            <div className="flex gap-2">
              {(dataInicio || dataFim) && (
                <button onClick={() => { setDataInicio(''); setDataFim(''); }}
                  className="px-3 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-[#F0F4FF]/60 transition-colors">
                  Limpar datas
                </button>
              )}
              <button onClick={fetchViagens} data-testid="btn-buscar-viagens"
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-medium transition-colors">
                Buscar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Carregando...</div>
        ) : viagens.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Bus className="w-12 h-12 mb-3 opacity-20" />
            <p className="font-medium">Nenhuma viagem encontrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Viagem</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Data</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Veículo</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Assentos</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginated.map((v) => (
                    <tr key={v.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`viagem-row-${v.id}`}>
                      <td className="px-5 py-3.5">
                        <p className="font-semibold text-slate-800">{v.titulo}</p>
                        <div className="flex items-center gap-1 mt-0.5 text-slate-400 text-xs">
                          <MapPin className="w-3 h-3" />
                          <span>{v.origem} → {v.destino}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <div className="flex items-center gap-1 text-slate-600 text-xs">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          {new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')} {v.horario}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-slate-500 hidden lg:table-cell text-xs">{v.veiculo}</td>
                      <td className="px-5 py-3.5">
                        <div className="text-xs text-slate-600">
                          <span className="font-medium text-emerald-600">{v.assentos_livres} livres</span>
                          <span className="text-slate-400"> / </span>
                          <span className="font-medium text-red-500">{v.assentos_ocupados} ocupados</span>
                        </div>
                        <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden mt-1">
                          <div className="h-full bg-orange-500 rounded-full"
                            style={{ width: `${v.capacidade > 0 ? (v.assentos_ocupados / v.capacidade) * 100 : 0}%` }} />
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusCls(v.status)}`}>
                          {getStatusLabel(v.status)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleViewDetail(v.id)} data-testid={`btn-view-viagem-${v.id}`}
                            title="Ver detalhes"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => openExportModal(v.id, 'motorista')} data-testid={`btn-motorista-viagem-${v.id}`}
                            title="Lista para Motorista"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors">
                            <Printer className="w-4 h-4" />
                          </button>
                          <button onClick={() => openExportModal(v.id, 'bagagem')} data-testid={`btn-bagagem-viagem-${v.id}`}
                            title="Lista de Bagagem"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                            <Package className="w-4 h-4" />
                          </button>
                          <button onClick={() => openExportModal(v.id, 'encomendas')} data-testid={`btn-encomendas-viagem-${v.id}`}
                            title="Lista de Encomendas"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors">
                            <Truck className="w-4 h-4" />
                          </button>
                          <button onClick={() => openExportModal(v.id, 'relatorio')} data-testid={`btn-relatorio-viagem-${v.id}`}
                            title="Relatório de Passageiros"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                            <FileText className="w-4 h-4" />
                          </button>
                          <button onClick={() => openExportModal(v.id, 'embarque')} data-testid={`btn-embarque-viagem-${v.id}`}
                            title="Lista de Embarque"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
                            <ClipboardList className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEdit(v)} data-testid={`btn-edit-viagem-${v.id}`}
                            title="Editar"
                            className={`p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors ${!canWrite ? 'hidden' : ''}`}>
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDeleteId(v.id)} data-testid={`btn-delete-viagem-${v.id}`}
                            title="Excluir"
                            className={`p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors ${!canWrite ? 'hidden' : ''}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
                <span className="text-xs text-slate-500">Mostrando {(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE, viagens.length)} de {viagens.length}</span>
                <div className="flex gap-2">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                    className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
                    className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="viagem-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingId ? 'Editar Viagem' : 'Nova Viagem'}
              </h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Título da Viagem *</label>
                <input value={form.titulo} onChange={(e) => f('titulo', e.target.value)} required
                  data-testid="viagem-titulo-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Origem *</label>
                  <input value={form.origem} onChange={(e) => f('origem', e.target.value)} required
                    data-testid="viagem-origem-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Destino *</label>
                  <input value={form.destino} onChange={(e) => f('destino', e.target.value)} required
                    data-testid="viagem-destino-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Data *</label>
                  <input type="date" value={form.data} onChange={(e) => f('data', e.target.value)} required
                    data-testid="viagem-data-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Horário *</label>
                  <input type="time" value={form.horario} onChange={(e) => f('horario', e.target.value)} required
                    data-testid="viagem-horario-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Veículo *</label>
                  <input value={form.veiculo} onChange={(e) => f('veiculo', e.target.value)} required
                    placeholder="Ex: Ônibus Mercedes 44px" data-testid="viagem-veiculo-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Motorista</label>
                  <input value={form.motorista || ''} onChange={(e) => f('motorista', e.target.value)}
                    data-testid="viagem-motorista-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Capacidade (assentos) *</label>
                  <input type="number" min="1" max="100" value={form.capacidade} onChange={(e) => f('capacidade', e.target.value)} required
                    data-testid="viagem-capacidade-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                  <select value={form.status} onChange={(e) => f('status', e.target.value)} data-testid="viagem-status-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 bg-white">
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Observações</label>
                <textarea value={form.observacoes || ''} onChange={(e) => f('observacoes', e.target.value)}
                  rows={2} data-testid="viagem-obs-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} data-testid="btn-salvar-viagem"
                  className="flex-1 bg-[#d72d21] hover:bg-[#b91c1c] text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60">
                  {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Criar Viagem')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {viewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="viagem-detail-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{viewModal.titulo}</h2>
              <button onClick={() => setViewModal(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Rota</p>
                  <p className="font-semibold text-slate-800 text-sm">{viewModal.origem} → {viewModal.destino}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Data e Hora</p>
                  <p className="font-semibold text-slate-800 text-sm">
                    {new Date(viewModal.data + 'T00:00:00').toLocaleDateString('pt-BR')} às {viewModal.horario}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Assentos</p>
                  <p className="text-sm">
                    <span className="font-semibold text-emerald-600">{viewModal.assentos_livres} livres</span>
                    <span className="text-slate-400 mx-1">/</span>
                    <span className="font-semibold text-red-500">{viewModal.assentos_ocupados} ocupados</span>
                    <span className="text-slate-400 mx-1">/</span>
                    <span className="text-slate-600">{viewModal.capacidade} total</span>
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusCls(viewModal.status)}`}>
                    {getStatusLabel(viewModal.status)}
                  </span>
                </div>
              </div>
              {/* Mapa visual de poltronas */}
              <MiniSeatMap
                capacidade={viewModal.capacidade}
                reservas={viewModal.reservas}
                onPrintSeat={(assento) => downloadPDF(viewModal.id, 'embarque', assento)}
                onRecibo={(reservaId, nome) => downloadRecibo(reservaId, nome)}
              />

              {viewModal.reservas?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 text-sm mb-2">
                    Reservas ({viewModal.reservas.filter(r => r.status !== 'cancelada').length})
                  </h3>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-slate-500 font-semibold">Pol.</th>
                          <th className="text-left px-3 py-2 text-slate-500 font-semibold">Passageiro</th>
                          <th className="text-left px-3 py-2 text-slate-500 font-semibold hidden sm:table-cell">Pagamento</th>
                          <th className="text-left px-3 py-2 text-slate-500 font-semibold">Status</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {[...viewModal.reservas]
                          .sort((a, b) => (a.assento || 0) - (b.assento || 0))
                          .map(r => {
                            const { cls, label } = getReservaStatusStyle(r.status, r.forma_pagamento);
                            const wa = waLink(r.cliente?.telefone);
                            return (
                              <tr key={r.id} className={r.status === 'cancelada' ? 'opacity-40' : 'hover:bg-slate-50/70 transition-colors'}>
                                <td className="px-3 py-2">
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-slate-100 font-bold text-slate-700">{r.assento}</span>
                                </td>
                                <td className="px-3 py-2">
                                  <p className="font-medium text-slate-800 leading-tight">{r.cliente?.nome || '—'}</p>
                                  {r.cliente?.telefone && (
                                    <p className="text-slate-400 text-[10px] mt-0.5">{r.cliente.telefone}</p>
                                  )}
                                </td>
                                <td className="px-3 py-2 hidden sm:table-cell">
                                  {r.forma_pagamento ? (
                                    <span className="text-slate-600">{r.forma_pagamento}</span>
                                  ) : (
                                    <span className="text-slate-300">—</span>
                                  )}
                                  {r.valor > 0 && (
                                    <p className="text-slate-400 text-[10px] mt-0.5">R$ {Number(r.valor).toFixed(2)}</p>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`px-1.5 py-0.5 rounded font-medium ${cls}`}>{label}</span>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-1">
                                    {wa && (
                                      <a href={wa} target="_blank" rel="noreferrer" title="Abrir WhatsApp"
                                        className="p-1 rounded text-green-600 hover:bg-green-50 transition-colors">
                                        <MessageCircle className="w-3.5 h-3.5" />
                                      </a>
                                    )}
                                    {r.status !== 'cancelada' && (
                                      <button
                                        title="Imprimir passagem individual"
                                        onClick={() => downloadPDF(viewModal.id, 'embarque', r.assento)}
                                        className="p-1 rounded text-[#0147a0] hover:bg-blue-50 transition-colors">
                                        <Ticket className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {r.status !== 'cancelada' && r.id && (
                                      <button
                                        title="Gerar recibo PDF"
                                        onClick={() => downloadRecibo(r.id, r.cliente?.nome)}
                                        className="p-1 rounded text-slate-500 hover:bg-slate-100 transition-colors">
                                        <Receipt className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Print / PDF Actions */}
              <div className="border-t border-slate-100 pt-4">
                <h3 className="font-semibold text-slate-700 text-sm mb-3">Imprimir / PDF</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => openExportModal(viewModal.id, 'motorista')}
                    data-testid="btn-lista-motorista-modal"
                    className="flex items-center gap-2 bg-[#0147a0] hover:bg-[#003375] text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                    Lista para Motorista
                  </button>
                  <button
                    onClick={() => openExportModal(viewModal.id, 'bagagem')}
                    data-testid="btn-lista-bagagem-modal"
                    className="flex items-center gap-2 border border-amber-300 text-amber-700 hover:bg-amber-50 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    <Package className="w-4 h-4" />
                    Lista de Bagagem
                  </button>
                  <button
                    onClick={() => openExportModal(viewModal.id, 'encomendas')}
                    data-testid="btn-lista-encomendas-modal"
                    className="flex items-center gap-2 border border-teal-300 text-teal-700 hover:bg-teal-50 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    <Truck className="w-4 h-4" />
                    Lista de Encomendas
                  </button>
                  <button
                    onClick={() => openExportModal(viewModal.id, 'embarque')}
                    data-testid="btn-bilhetes-modal"
                    className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    <ClipboardList className="w-4 h-4" />
                    Bilhetes de Embarque
                  </button>
                  <button
                    onClick={() => openExportModal(viewModal.id, 'relatorio')}
                    data-testid="btn-relatorio-modal"
                    className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Relatório Completo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Format Modal (PDF ou Excel) */}
      <ExportFormatModal
        open={exportModal.open}
        label={exportModal.label}
        loadingExcel={loadingExcel}
        onClose={closeExportModal}
        onPDF={() => downloadPDF(exportModal.viagemId, exportModal.type)}
        onExcel={() => downloadExcel(exportModal.viagemId, exportModal.type)}
      />

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Deseja excluir esta viagem? As reservas vinculadas não serão excluídas automaticamente.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
